import {useMutation, useQueryClient} from '@github-ui/react-query'
import {createEstablishedAhpInitialSubmissionCoordinator} from '@github-ui/ahp-session-client/initial-submission'
import type {PreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import {fetchTask} from './use-task'
import {readTasks, writeTasks, type TaskListPage} from './repo-task-utils'
import type {HydratedAPITask} from '../types/api-task'
import type {McpPermissionMode} from '@github-ui/agent-sessions-types/mcp-permission-mode'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import type {OptimisticTask} from '../types/task'
import {AhpChatStore} from '@github-ui/ahp-relay/ahp-chat-store'
import {reportRelayError} from '@github-ui/ahp-relay/relay-registry'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isCloudSessionStoreEnabled} from '@github-ui/copilot-shared-ui/utils/cloud-session-store-flag'
import {mapMcpPermissionModeToAgentMode} from '../utils/mcp-permission-mode'
import {mcFetcher} from '../utils/mc-fetcher'
import {repoCloneUrl} from '../utils/repo-clone-url'
import {sortTasksByDate, transformAPITaskToUnhydrated} from '../utils/task-helpers'
import {uuid} from '../utils/uuid'
import {createMissionControlEstablishedAhpBootstrap} from '../ahp/established-ahp-bootstrap'
import {createAhpRelayDiagnostics} from '../ahp/ahp-debug-log'
import {beginTaskStartup, completeTaskStartup, failTaskStartup} from '../ahp/task-startup-store'
import {createEnvironmentBoundTask, type CreateEnvironmentBoundTaskInput} from '../ahp/environment-task'
import {SANDBOX_ENVIRONMENT_ID} from '@github-ui/one-copilot-composer/environment-strip/environment-option'

export interface CreateEnvironmentTaskInput {
  environmentId: string
  prompt: PreparedPrompt
  /** Repository (name-with-owner) to associate + provision as a host project. */
  repositoryNwo?: string
  /** Server-provided HTTPS clone URL; host-correct, preferred over the origin-derived {@link repoCloneUrl}. */
  repositoryCloneUrl?: string
  /** Base branch for the worktree; the host auto-generates the working branch. */
  baseRef?: string
  /** Managed sandbox VM; client-side host-project provisioning is flag-gated. */
  managedSandbox?: boolean
  /** Exact CLI mode selected by the unified composer. */
  agentMode?: AgentMode
  /**
   * Optional MCP permission mode captured by the composer. When set to a
   * non-default value, the first-turn coordinator applies the equivalent
   * chat runtime mode via `session.commands.setMode` before accepting the
   * user's opening turn, mirroring the Quick Chat handoff.
   */
  mcpPermissionMode?: McpPermissionMode
  /** Login used to scope repository-less managed sandbox compute. */
  scopeLogin?: string
  /** Optional Copilot Space selected for repository-less Chat. */
  space?: {owner: string; slug: string}
  clientGeneratedId?: string
}

export interface CreateEnvironmentTaskResult {
  task: HydratedAPITask
  sessionId: string
  environmentId: string
}

/**
 * Cache integration that mirrors the cloud `useCreateAPITask` shape so each
 * page can pass the same optimistic config it already builds for cloud task
 * creation. When omitted, the hook still works — it just doesn't touch any
 * task list cache.
 */
export interface OptimisticTaskListConfig {
  /** Query key whose data this hook should optimistically modify. */
  queryKey: readonly unknown[]
  /** Additional query keys to invalidate (not optimistically update) after a
   * successful creation — e.g. the grouped-tasks sidebar cache. */
  additionalInvalidateKeys?: ReadonlyArray<readonly unknown[]>
  /** Maximum number of tasks to keep in the list after the optimistic
   * placeholder is replaced; matches the page's pagination size so the list
   * doesn't overflow. */
  pageSize: number
}

export interface UseCreateEnvironmentTaskOptions {
  optimisticTaskListConfig?: OptimisticTaskListConfig
}

interface MutationContext {
  clientGeneratedId: string
}

export function useCreateEnvironmentTask(options?: UseCreateEnvironmentTaskOptions) {
  const queryClient = useQueryClient()
  const config = options?.optimisticTaskListConfig

  return useMutation<CreateEnvironmentTaskResult, Error, CreateEnvironmentTaskInput, MutationContext>({
    onMutate: async (input): Promise<MutationContext> => {
      const clientGeneratedId = input.clientGeneratedId ?? uuid()
      input.clientGeneratedId = clientGeneratedId
      if (!config) return {clientGeneratedId}

      await queryClient.cancelQueries({queryKey: config.queryKey})
      const optimistic = createOptimisticEnvironmentTask({
        clientGeneratedId,
        problemStatement: input.prompt.text,
      })
      queryClient.setQueryData<TaskListPage>(config.queryKey, oldData =>
        writeTasks(oldData, [optimistic, ...readTasks(oldData)].slice(0, config.pageSize)),
      )
      return {clientGeneratedId}
    },
    mutationFn: async ({
      environmentId,
      prompt,
      repositoryNwo,
      repositoryCloneUrl,
      baseRef,
      managedSandbox,
      agentMode,
      mcpPermissionMode,
      scopeLogin,
      space,
      clientGeneratedId,
    }): Promise<CreateEnvironmentTaskResult> => {
      const normalizedScopeLogin = scopeLogin?.trim()
      if (environmentId === SANDBOX_ENVIRONMENT_ID && !repositoryNwo && !normalizedScopeLogin) {
        throw new Error('Select a personal or organization scope before starting Chat.')
      }
      const environmentTaskInput: CreateEnvironmentBoundTaskInput = repositoryNwo
        ? {
            environmentId,
            problemStatement: prompt.text,
            repositoryNwo,
            ...(baseRef ? {baseRef} : {}),
          }
        : {
            environmentId,
            problemStatement: prompt.text,
            ...(normalizedScopeLogin ? {computeScope: normalizedScopeLogin} : {}),
            ...(space ? {space} : {}),
          }
      const {taskId, sessionId, boundEnvironmentId} = await createEnvironmentBoundTask(environmentTaskInput)

      if (config && clientGeneratedId) {
        patchOptimisticTaskId(queryClient, config, clientGeneratedId, taskId)
      }

      const relayEnvironmentId = boundEnvironmentId ?? environmentId
      const identity = {taskId, environmentId: relayEnvironmentId, sessionId}
      beginTaskStartup(taskId, prompt)

      const bootstrap = createMissionControlEstablishedAhpBootstrap({
        provisionSession: async () => {
          throw new Error('Environment task bootstrap cannot provision a second task.')
        },
        createRelayOptions: (relayIdentity, request) => ({
          fetcher: mcFetcher,
          createSink: () => new AhpChatStore(),
          ...createAhpRelayDiagnostics(relayIdentity.taskId ?? relayIdentity.sessionId),
          // `recover` is this path's first-establish mode (the coordinator always has an
          // identity), so this is where the relay actually creates the daemon session.
          // `attach`/`wake` deliberately omit `createSession` so reconnects only subscribe.
          ...(request?.mode === 'recover'
            ? {
                createSession: {
                  // Grants the SDK `session-store` capability that backs `/chronicle`.
                  // copilot-host reads it only at session creation and it cannot be
                  // added afterwards, so it has to ride along here.
                  ...(isCloudSessionStoreEnabled() ? {enableSessionStore: true} : {}),
                  // Sandbox VMs are pre-cloned by MC; client-side host-project
                  // provisioning is opt-in until the host advertises project management.
                  ...(repositoryNwo &&
                  (!managedSandbox || isFeatureEnabled('copilot_mission_control_sandbox_client_side_clone'))
                    ? {
                        project: {
                          nwo: repositoryNwo,
                          cloneUrl: repoCloneUrl(repositoryNwo, repositoryCloneUrl),
                          ...(baseRef ? {baseBranch: baseRef} : {}),
                        },
                      }
                    : {}),
                },
              }
            : {}),
        }),
      })
      const initialSubmission = createEstablishedAhpInitialSubmissionCoordinator({
        bootstrap,
        identity,
      })
      // Apply mode via configureSession so it's scoped to the first-turn coordinator, mirroring EstablishedChatComposer.
      // Wire it whenever the picker supplied a mode (including 'default' = Interactive) so we can override
      // the relay's connect-time approveAll=true default. Skipping 'default' here would leave tool calls
      // auto-approving even when the user explicitly picked Interactive.
      const configureSessionMode =
        agentMode ?? (mcpPermissionMode ? mapMcpPermissionModeToAgentMode(mcpPermissionMode) : undefined)
      void (async () => {
        try {
          const establishedSession = await initialSubmission.submit({
            prompt,
            origin: 'new',
            ...(configureSessionMode
              ? {
                  configureSession: async session => {
                    // setMode and setApproveAll are independent host RPCs. The AHP relay
                    // default-enables approveAll on connect (relay-registry.ts:422), so
                    // Interactive/Plan must also explicitly disable it or tool calls auto-approve.
                    // Setting it here marks the entry so the default can't re-fire when TaskChat's
                    // lease reuses the entry within the ref-count grace period.
                    await Promise.all([
                      session.commands.setMode(configureSessionMode),
                      session.commands.setApproveAll(configureSessionMode === 'autopilot'),
                    ])
                  },
                }
              : {}),
          })
          if (establishedSession.view.getSnapshot().passive) {
            throw new Error('This environment is read-only.')
          }
          completeTaskStartup(taskId)
        } catch (error) {
          failTaskStartup(taskId, error)
          reportRelayError(taskId, error)
        } finally {
          initialSubmission.dispose()
          bootstrap.dispose()
        }
      })()

      const task = await fetchTask({taskId})
      return {
        task,
        sessionId,
        environmentId: relayEnvironmentId,
      }
    },
    onSuccess: ({task}, _input, context) => {
      if (!config || !context) return
      const transformed = transformAPITaskToUnhydrated(task)
      queryClient.setQueryData<TaskListPage>(config.queryKey, oldData => {
        const cached = readTasks(oldData)
        const withoutDupes = cached.filter(
          t =>
            t.clientGeneratedId !== context.clientGeneratedId &&
            (!transformed || (t.id !== transformed.id && t.task_id !== transformed.id)),
        )
        const next = transformed ? sortTasksByDate([transformed, ...withoutDupes]) : withoutDupes
        return writeTasks(oldData, next.slice(0, config.pageSize))
      })
      queryClient.invalidateQueries({queryKey: config.queryKey, exact: true})

      for (const key of config.additionalInvalidateKeys ?? []) {
        queryClient.invalidateQueries({queryKey: key})
      }
    },
    onError: (_error, _input, context) => {
      if (!config || !context) return
      queryClient.setQueryData<TaskListPage>(config.queryKey, oldData =>
        writeTasks(
          oldData,
          readTasks(oldData).filter(t => t.clientGeneratedId !== context.clientGeneratedId),
        ),
      )
    },
  })
}

function patchOptimisticTaskId(
  queryClient: ReturnType<typeof useQueryClient>,
  config: OptimisticTaskListConfig,
  clientGeneratedId: string,
  taskId: string,
): void {
  queryClient.setQueryData<TaskListPage>(config.queryKey, oldData => {
    const cached = readTasks(oldData)
    if (!cached.some(task => task.clientGeneratedId === clientGeneratedId)) return oldData
    return writeTasks(
      oldData,
      cached.map(task =>
        task.clientGeneratedId === clientGeneratedId ? {...task, id: taskId, task_id: taskId} : task,
      ),
    )
  })
}

interface CreateOptimisticEnvironmentTaskParams {
  clientGeneratedId: string
  problemStatement: string
}

export function createOptimisticEnvironmentTask({
  clientGeneratedId,
  problemStatement,
}: CreateOptimisticEnvironmentTaskParams): OptimisticTask {
  return {
    resource_type: 'pull',
    resource_id: -1,
    resource_global_id: '',
    created_at: new Date().toISOString(),
    session_count: 1,
    latest_session_updated_at: new Date().toISOString(),
    latest_session_state: 'in_progress',
    isOptimistic: true,
    clientGeneratedId,
    problemStatement,
    name: problemStatement,
  }
}
